import React, { useMemo } from 'react';

import type { UUID } from '@aline/common';
import { Skeleton } from '@mui/material';
import type { ActivityItemFragment } from 'src/components';
import { ActivityFeed, ActivitySubscribers } from 'src/components';
import { makeStyles } from 'tss-react/mui';

import { ActivityLaneSwitch } from './ActivityLaneSwitch';
import { TaskComposerDraftContext } from './task-composer-draft';
import { getTaskDocuments } from './task-review-helpers';
import { TaskActivityComposer } from './TaskActivityComposer';
import { TaskAiAssigneeRow } from './TaskAiAssigneeRow';
import { TaskBreadcrumbs } from './TaskBreadcrumbs';
import { TaskDocuments } from './TaskDocuments';
import { TaskNextStep } from './TaskNextStep';
import type { TaskReviewAssignmentsHandle } from './TaskReviewAssignments';
import { TaskReviewAssignments } from './TaskReviewAssignments';
import { TaskReviewFooter } from './TaskReviewFooter';
import { TaskReviewStatusAlert } from './TaskReviewStatusAlert';
import { TaskStatusRow } from './TaskStatusRow';
import { TaskSubtaskList } from './TaskSubtaskList';
import type { TaskWithComments } from './use-task-panel-state';
import { useTaskPanelState } from './use-task-panel-state';
import { TaskAssigneePicker } from '../TaskAssigneePicker';
import { TaskDescriptionField } from '../TaskDescriptionField';
import { TaskInstructions } from '../TaskInstructions';
import { TaskTitleField } from '../TaskTitleField';

const useStyles = makeStyles()(theme => ({
  // Holds the composer and footer under the scroller, centred at the task's width.
  column: {
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    margin: '0 auto',
    width: '100%',
  },
  composerDock: {
    flexShrink: 0,
    // Sides match the scroll content; bottom padding keeps the card clear of the footer's shadow.
    padding: theme.spacing(1, 2, 1.5, 2),
  },
  content: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing(1),
    minHeight: 0,
    overflow: 'auto',
    padding: theme.spacing(2, 2, 0, 2),
  },
  // Spans the panel so the feed's scrollbar sits at its edge; the feed insets its own content.
  feedShell: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
    paddingTop: theme.spacing(2),
  },
  root: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
  },
  taskFields: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
  },
}));

interface TaskPanelBodyProps {
  onOpenTask: (taskId: UUID) => void;
  task: TaskWithComments;
  workflowId: UUID;
  /** Caps the task's width below the full-width status alert. */
  contentMaxWidth?: number;
  /** The document the panel sits beside, so a redline upload defaults to it. */
  currentDocumentId?: UUID | null;
  /** Downloads the open document from the editor, which can be ahead of its last saved version. */
  downloadCurrentDocument?: () => void;
  isAiBuilding?: boolean;
  /** Shows the task as its external assignees see it, read-only. */
  isPreview?: boolean;
}

/** The task in the workflow side panel: one column with the fields above the activity feed. */
export const TaskPanelBody = React.memo(function TaskPanelBody({
  contentMaxWidth,
  currentDocumentId = null,
  downloadCurrentDocument,
  isAiBuilding = false,
  isPreview = false,
  onOpenTask,
  task,
  workflowId,
}: TaskPanelBodyProps) {
  const { classes, theme } = useStyles();
  const state = useTaskPanelState({ isAiBuilding, isPreview, task, workflowId });
  const { activityController, activityLane, isExternalView, isReadOnly, reviewersRef } = state;

  const fields = (
    <TaskPanelFields
      currentDocumentId={currentDocumentId}
      downloadCurrentDocument={downloadCurrentDocument}
      isExternalView={isExternalView}
      isPreview={isPreview}
      isReadOnly={isReadOnly}
      reviewersRef={reviewersRef}
      task={task}
      workflowId={workflowId}
      onAlineAiActivityStarted={state.handleAlineAiActivityStarted}
      onOpenTask={onOpenTask}
    />
  );
  const footer = (
    <TaskReviewFooter
      isPreview={isPreview}
      readOnly={isAiBuilding}
      task={task}
      workflow={task.workflow}
    />
  );

  const inlinePadding = getInlinePadding(contentMaxWidth, theme.spacing(2));

  if (state.isTemplateTask)
    return (
      <div className={classes.root}>
        <div
          key={task.id}
          className={classes.content}
          style={{ paddingLeft: inlinePadding, paddingRight: inlinePadding }}
        >
          {fields}
        </div>
        <div className={classes.column} style={{ maxWidth: contentMaxWidth }}>
          {footer}
        </div>
      </div>
    );

  return (
    <TaskComposerDraftContext value={state.composerDraft}>
      <div className={classes.root}>
        {!isExternalView && (
          <TaskReviewStatusAlert
            task={task}
            onJumpToReviewers={
              task.assignments.length ? () => reviewersRef.current?.reveal() : null
            }
          />
        )}
        <div key={task.id} className={classes.feedShell}>
          <ActivityFeed
            ref={state.feedRef}
            stickyHeader
            fetchMoreActivity={activityController.fetchMoreActivity}
            fetchPreviousActivity={activityController.fetchPreviousActivity}
            hasMore={activityController.hasMore}
            hasPrevious={activityController.hasPrevious}
            headerAction={!isExternalView && <ActivitySubscribers taskId={task.id} />}
            headerControl={
              activityLane.showSwitch && (
                <ActivityLaneSwitch lane={activityLane.lane} onChange={activityLane.setLane} />
              )
            }
            inlinePadding={inlinePadding}
            items={activityController.activity}
            leadingContent={fields}
            loadedAnchor={activityController.loadedAnchor}
            loading={activityController.loading}
            loadingMore={activityController.loadingMore}
            loadingPrevious={activityController.loadingPrevious}
            navigation={activityController.navigation}
            // The task's fields sit above the feed and are what the reader opens the panel for.
            opensAtNewest={false}
            workflowId={activityController.workflowId}
            onSubmitReply={activityController.onSubmitReply}
            onViewThread={state.onViewThread}
          />
        </div>
        <div className={classes.column} style={{ maxWidth: contentMaxWidth }}>
          {/* Composer docks above the footer and never scrolls, regardless of feed length. */}
          <div className={classes.composerDock}>
            <TaskActivityComposer isPreview={isPreview} state={state} task={task} />
          </div>
          {footer}
        </div>
      </div>
    </TaskComposerDraftContext>
  );
});

export function TaskPanelSkeleton(): JSX.Element {
  const { classes } = useStyles();
  return (
    <div className={classes.content}>
      <Skeleton variant="text" width={80} />
      <Skeleton height={40} sx={{ alignSelf: 'flex-end' }} variant="circular" width={40} />
      <Skeleton variant="text" width={64} />
      <Skeleton height={48} sx={{ borderRadius: 1 }} variant="rectangular" width="100%" />
      <Skeleton variant="text" width={64} />
      <Skeleton height={80} sx={{ borderRadius: 1 }} variant="rectangular" width="100%" />
    </div>
  );
}

interface TaskPanelFieldsProps {
  currentDocumentId: UUID | null;
  downloadCurrentDocument: (() => void) | undefined;
  isExternalView: boolean;
  isPreview: boolean;
  isReadOnly: boolean;
  onAlineAiActivityStarted: (activityItem: ActivityItemFragment) => void;
  onOpenTask: (taskId: UUID) => void;
  reviewersRef: React.Ref<TaskReviewAssignmentsHandle>;
  task: TaskWithComments;
  workflowId: UUID;
}

function TaskPanelFields({
  currentDocumentId,
  downloadCurrentDocument,
  isExternalView,
  isPreview,
  isReadOnly,
  onAlineAiActivityStarted,
  onOpenTask,
  reviewersRef,
  task,
  workflowId,
}: TaskPanelFieldsProps) {
  const { classes } = useStyles();
  const documents = useMemo(() => getTaskDocuments(task), [task]);
  const editDisabledReason = isPreview ? 'Preview only' : task.requesterDisallowedReason.edit;

  return (
    <div className={classes.taskFields}>
      {task.parentTask && (
        <TaskBreadcrumbs parentTask={task.parentTask} title={task.title} onOpenTask={onOpenTask} />
      )}

      {isExternalView && (
        <TaskNextStep
          documentCount={documents.length}
          isExternalView={isExternalView}
          isPreview={isPreview}
          task={task}
        />
      )}

      {isExternalView && documents.length > 0 && (
        <TaskDocuments
          currentDocumentId={currentDocumentId}
          documents={documents}
          downloadCurrentDocument={downloadCurrentDocument}
          isPreview={isPreview}
          isReadOnly={isReadOnly}
          task={task}
        />
      )}

      <TaskTitleField
        editDisabledReason={editDisabledReason}
        isReadOnly={isReadOnly || isExternalView}
        taskId={task.id}
        title={task.title}
      />

      {/* The task's overall status reflects the internal side's process, so externals don't see it. */}
      {!isExternalView && <TaskStatusRow task={task} />}

      {isExternalView && <TaskInstructions externalDescription={task.externalDescription} />}

      {!isExternalView && (
        <TaskDescriptionField
          description={task.description}
          editDisabledReason={editDisabledReason}
          externalDescription={task.externalDescription}
          isReadOnly={isReadOnly}
          taskId={task.id}
          workflowId={workflowId}
        />
      )}

      {!isExternalView && (
        <TaskReviewAssignments
          ref={reviewersRef}
          aiRow={
            task.isDelegatedToAi && (
              <TaskAiAssigneeRow
                editDisabledReason={editDisabledReason}
                isReadOnly={isReadOnly}
                taskId={task.id}
              />
            )
          }
          assigneePicker={
            <TaskAssigneePicker
              editDisabledReason={editDisabledReason}
              isReadOnly={isReadOnly}
              task={task}
              workflowId={workflowId}
              onAlineAiActivityStarted={onAlineAiActivityStarted}
            />
          }
          changeStatusDisabledReason={
            isPreview ? 'Preview only' : (task.requesterDisallowedReason.changeStatus ?? null)
          }
          editDisabledReason={editDisabledReason ?? null}
          isReadOnly={isReadOnly}
          task={task}
          workflowId={workflowId}
        />
      )}

      {!task.parentTask && (
        <TaskSubtaskList
          canEdit={!isExternalView && !isReadOnly && !editDisabledReason}
          parentTaskId={task.id}
          phaseId={task.phase.id}
          subtasks={task.subtasks}
          workflowId={workflowId}
          onOpenTask={onOpenTask}
        />
      )}
    </div>
  );
}

/**
 * Side padding that keeps the task `contentMaxWidth` wide and centred inside a full-width
 * scroller, never less than `gutter`. Matches the capped column the composer and footer sit in.
 */
function getInlinePadding(contentMaxWidth: number | undefined, gutter: string): string {
  if (!contentMaxWidth) return gutter;
  return `max(${gutter}, calc((100% - ${contentMaxWidth}px) / 2 + ${gutter}))`;
}
